import {
	DARK_THEMES,
	LIGHT_THEMES,
	THEMES,
	THEME_MODES,
	THEME_ORDER,
	useTheme,
} from "../ThemeContext";

const themePreviewColors = {
	[THEMES.LIGHT]: ["#ffffff", "#f3f4f6", "#3b82f6"],
	[THEMES.DARK]: ["#1f2937", "#111827", "#3b82f6"],
	[THEMES.MONOKAI]: ["#272822", "#49483E", "#66D9EF"],
	[THEMES.DRACULA]: ["#282a36", "#44475a", "#bd93f9"],
	[THEMES.NORD]: ["#2e3440", "#434c5e", "#88c0d0"],
	[THEMES.SOLARIZED]: ["#fdf6e3", "#eee8d5", "#268bd2"],
	[THEMES.GITHUB]: ["#ffffff", "#f1f8ff", "#005cc5"],
	[THEMES.NIGHTOWL]: ["#011627", "#1d3b53", "#82aaff"],
	[THEMES.SYNTHWAVE]: ["#2d2b55", "#34294f", "#ff7edb"],
	[THEMES.AURA]: ["#15141b", "#2d2b38", "#a277ff"],
};

const themeNames = {
	[THEMES.LIGHT]: "Light",
	[THEMES.DARK]: "Dark",
	[THEMES.MONOKAI]: "Monokai",
	[THEMES.DRACULA]: "Dracula",
	[THEMES.NORD]: "Nord",
	[THEMES.SOLARIZED]: "Solarized Light",
	[THEMES.GITHUB]: "GitHub",
	[THEMES.NIGHTOWL]: "Night Owl",
	[THEMES.SYNTHWAVE]: "Synthwave",
	[THEMES.AURA]: "Aura Dark",
};

const modeLabels = {
	[THEME_MODES.LIGHT]: "Light",
	[THEME_MODES.DARK]: "Dark",
	[THEME_MODES.SYSTEM]: "System",
};

const ThemeGrid = ({ themes, selectedTheme, onSelect, label }) => (
	<fieldset className="grid grid-cols-2 sm:grid-cols-3 gap-4">
		<legend className="sr-only">{label}</legend>
		{themes.map((theme) => {
			const themeName = themeNames[theme];
			const colors = themePreviewColors[theme];
			const isSelected = selectedTheme === theme;

			return (
				<button
					key={theme}
					type="button"
					onClick={() => onSelect(theme)}
					className={`flex flex-col items-center p-3 rounded-lg border transition-all ${
						isSelected
							? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]"
							: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
					}`}
					aria-label={`Select ${themeName} theme`}
					aria-pressed={isSelected}
				>
					<div className="w-full h-12 rounded-md overflow-hidden flex mb-2 shadow-sm">
						<div className="w-2/3" style={{ backgroundColor: colors[0] }} />
						<div className="w-1/3" style={{ backgroundColor: colors[1] }} />
						<div className="w-1/12" style={{ backgroundColor: colors[2] }} />
					</div>
					<span className="text-xs font-medium text-[var(--color-text-primary)]">
						{themeName}
					</span>
				</button>
			);
		})}
	</fieldset>
);

export const ThemeSelector = ({ enableSystemThemeControls = true }) => {
	const {
		theme,
		setTheme,
		themeMode,
		setThemeMode,
		lightTheme,
		setLightTheme,
		darkTheme,
		setDarkTheme,
	} = useTheme();

	if (!enableSystemThemeControls) {
		return (
			<div className="py-4">
				<ThemeGrid
					themes={THEME_ORDER}
					selectedTheme={theme}
					onSelect={setTheme}
					label="Theme"
				/>
			</div>
		);
	}

	return (
		<div className="space-y-6 py-4">
			<div className="space-y-2">
				<div className="text-sm font-medium text-[var(--color-text-primary)]">
					Theme mode
				</div>
				<fieldset className="grid grid-cols-3 gap-2">
					<legend className="sr-only">Theme mode</legend>
					{Object.values(THEME_MODES).map((mode) => {
						const isSelected = themeMode === mode;
						const modeTheme =
							mode === THEME_MODES.LIGHT ? lightTheme : darkTheme;
						const description =
							mode === THEME_MODES.SYSTEM
								? `${themeNames[theme]} now`
								: themeNames[modeTheme];

						return (
							<button
								key={mode}
								type="button"
								onClick={() => setThemeMode(mode)}
								className={`rounded-lg border px-3 py-2 text-left transition-all ${
									isSelected
										? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]"
										: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
								}`}
								aria-pressed={isSelected}
							>
								<span className="block text-sm font-medium text-[var(--color-text-primary)]">
									{modeLabels[mode]}
								</span>
								<span className="block text-xs text-[var(--color-text-secondary)]">
									{description}
								</span>
							</button>
						);
					})}
				</fieldset>
				{themeMode === THEME_MODES.SYSTEM && (
					<p className="text-xs text-[var(--color-text-secondary)]">
						Following your OS preference. Currently applying the{" "}
						{themeNames[theme]} theme.
					</p>
				)}
			</div>

			<div className="space-y-2">
				<div className="text-sm font-medium text-[var(--color-text-primary)]">
					Light theme
				</div>
				<ThemeGrid
					themes={LIGHT_THEMES}
					selectedTheme={lightTheme}
					onSelect={setLightTheme}
					label="Light theme"
				/>
			</div>

			<div className="space-y-2">
				<div className="text-sm font-medium text-[var(--color-text-primary)]">
					Dark theme
				</div>
				<ThemeGrid
					themes={DARK_THEMES}
					selectedTheme={darkTheme}
					onSelect={setDarkTheme}
					label="Dark theme"
				/>
			</div>
		</div>
	);
};
